import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import FooterCTA from "../components/FooterCTA";
import useDocumentMeta from "../lib/useDocumentMeta";
import { buildWhatsAppLink } from "../lib/constants";
import { INDUSTRIES } from "../lib/industriesData";

const waFor = (ind) =>
    buildWhatsAppLink(
        `Hi Raj Graphics! I run a ${ind.name.toLowerCase()} business and I'd like to discuss branding / design / printing for it.`,
    );

const IndustriesPage = () => {
    useDocumentMeta({
        title: "Industries We Serve — Branding & Design for Nagpur Businesses | Raj Graphics",
        description:
            "Raj Graphics designs for retail, restaurants, real estate, healthcare, education, manufacturing and more — branding, printing and signage for businesses in Nagpur and across India.",
        canonical: "/industries",
    });

    return (
        <main data-testid="industries-page" className="bg-[#050505]">
            <section className="relative pt-36 pb-12 md:pt-44 md:pb-16 overflow-hidden">
                <div className="absolute inset-0 grid-pattern radial-fade opacity-60 pointer-events-none" />
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-gold/[0.07] blur-[140px] pointer-events-none" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12">
                    <div className="font-inter text-xs uppercase tracking-[0.25em] text-gold mb-5">
                        Industries
                    </div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="font-poppins font-bold tracking-tight text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] max-w-4xl"
                        data-testid="industries-headline"
                    >
                        Every business is different. <span className="gold-gradient-text">So is its design.</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="mt-6 font-inter text-base sm:text-lg text-zinc-400 max-w-2xl leading-relaxed"
                    >
                        From a café's menu board to a builder's project brochure — we understand
                        what each industry needs to look credible, get noticed and sell. Based in
                        Nagpur, working with businesses everywhere.
                    </motion.p>
                </div>
            </section>

            <section className="relative py-12 md:py-16">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div
                        data-testid="industries-grid"
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6"
                    >
                        {INDUSTRIES.map((ind, i) => (
                            <motion.div
                                key={ind.id}
                                initial={{ opacity: 0, y: 24 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.15 }}
                                transition={{ duration: 0.5, delay: i * 0.05 }}
                                data-testid={`industry-card-${ind.id}`}
                                className="group relative flex flex-col bg-white/[0.02] border border-white/5 rounded-2xl p-7 md:p-8 hover:border-gold/40 hover:bg-white/[0.04] transition-all duration-300"
                            >
                                <div className="font-poppins text-xs text-zinc-600 mb-5">
                                    {String(i + 1).padStart(2, "0")}
                                </div>
                                <h2 className="font-poppins font-medium text-white text-xl">
                                    {ind.name}
                                </h2>
                                <p className="mt-3 font-inter text-sm text-zinc-400 leading-relaxed flex-1">
                                    {ind.desc}
                                </p>
                                <a
                                    href={waFor(ind)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`industry-cta-${ind.id}`}
                                    className="mt-6 self-start inline-flex items-center gap-2 text-gold font-inter text-sm hover:underline underline-offset-4"
                                >
                                    Discuss your project
                                    <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                                </a>
                            </motion.div>
                        ))}
                    </div>

                    <p className="mt-12 font-inter text-sm text-zinc-500 max-w-3xl leading-relaxed">
                        Don't see your industry? We've most likely worked on something similar —
                        message us on WhatsApp with a quick brief and we'll show you relevant work.
                    </p>
                </div>
            </section>

            <FooterCTA />
        </main>
    );
};

export default IndustriesPage;
